import { SelectInput } from '@my/ui';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  lineDefaultAmount,
  newLineKey,
  productOptionLabel,
  productPrefill,
  unitTotalsFromLines,
} from '../lib/cargoProducts';
import type { CargoItemDraft, CargoProductLineDraft, CargoProductOption } from '../lib/types';

export interface CargoProductLinesProps {
  index: number;
  draft: CargoItemDraft;
  options: CargoProductOption[];
  /** The cargo type has Compartments: several lines, dragged into order. */
  multiProduct: boolean;
  disabled: boolean;
  onChange: (patch: Partial<CargoItemDraft>) => void;
}

/**
 * What a cargo unit carries. One Product box normally — the unit's own
 * weight is the product's. With Compartments, an ordered list: drag ⠿ to
 * reorder (the order is the compartment order), each line with its own
 * amount; the unit's weight / volume become what the lines add up to.
 *
 * <p>
 * The unit itself (Tank ID, seal, status) stays the row around this — two
 * 20ft containers are two rows, each with its own list.
 * </p>
 */
export function CargoProductLines({
  index,
  draft,
  options,
  multiProduct,
  disabled,
  onChange,
}: CargoProductLinesProps) {
  const lines = draft.products;

  // A unit already holding several lines shows them as a list even if the
  // cargo type has since lost Compartments, rather than hiding all but one.
  if (!multiProduct && lines.length <= 1) {
    return (
      <SingleProduct index={index} draft={draft} options={options} disabled={disabled} onChange={onChange} />
    );
  }

  const setLines = (next: CargoProductLineDraft[]) =>
    onChange({ products: next, ...unitTotalsFromLines(next) });

  return (
    <ProductList
      index={index}
      lines={lines}
      options={options}
      canAdd={multiProduct}
      disabled={disabled}
      onChange={setLines}
    />
  );
}

// ── One product ─────────────────────────────────────────────────────────────

function SingleProduct({
  index,
  draft,
  options,
  disabled,
  onChange,
}: {
  index: number;
  draft: CargoItemDraft;
  options: CargoProductOption[];
  disabled: boolean;
  onChange: (patch: Partial<CargoItemDraft>) => void;
}) {
  const current = draft.products[0] ?? null;
  const currentOption = current ? options.find((o) => o.id === current.productId) : undefined;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <SelectInput
        label="Product"
        name={`product-${index}`}
        value={current?.productId ?? ''}
        disabled={disabled}
        options={productSelectOptions(options, current)}
        onChange={(e) => {
          const id = e.target.value;
          if (!id) {
            onChange({ products: [] });
            return;
          }
          const option = options.find((o) => o.id === id);
          onChange({
            // Same line, new product — the server re-copies it on save.
            products: [
              {
                key: current?.key ?? newLineKey(),
                id: current?.id ?? null,
                productId: id,
                productName: option?.name ?? current?.productName ?? null,
                amount: null,
                unit: 'kg',
              },
            ],
            // One product: its default fills the UNIT's empty weight / volume.
            ...(option ? productPrefill(option, draft) : {}),
          });
        }}
      />
      {currentOption && currentOption.wasteCodes.length > 0 && (
        <div className="flex items-center">
          <WasteCodes option={currentOption} />
        </div>
      )}
    </div>
  );
}

// ── Several products ────────────────────────────────────────────────────────

function ProductList({
  index,
  lines,
  options,
  canAdd,
  disabled,
  onChange,
}: {
  index: number;
  lines: CargoProductLineDraft[];
  options: CargoProductOption[];
  canAdd: boolean;
  disabled: boolean;
  onChange: (next: CargoProductLineDraft[]) => void;
}) {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const patch = (i: number, p: Partial<CargoProductLineDraft>) =>
    onChange(lines.map((l, j) => (j === i ? { ...l, ...p } : l)));

  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= lines.length) return;
    const next = lines.slice();
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    onChange(next);
  };

  const add = () =>
    onChange([
      ...lines,
      { key: newLineKey(), id: null, productId: '', productName: null, amount: null, unit: 'kg' },
    ]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          Products {lines.length > 1 && <span className="font-normal normal-case">· drag to set the compartment order</span>}
        </span>
        {canAdd && (
          <button
            type="button"
            onClick={add}
            disabled={disabled}
            className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Add product
          </button>
        )}
      </div>

      {lines.length === 0 && (
        <p className="text-[11px] italic text-slate-400">No product yet — use + Add product.</p>
      )}

      <ol className="space-y-1.5">
        {lines.map((line, i) => (
          <li
            key={line.key}
            draggable={!disabled}
            onDragStart={(e) => {
              setDragFrom(i);
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(e) => {
              if (dragFrom === null) return;
              e.preventDefault();
              setDragOver(i);
            }}
            onDragLeave={() => setDragOver((o) => (o === i ? null : o))}
            onDrop={(e) => {
              e.preventDefault();
              if (dragFrom !== null) move(dragFrom, i);
              setDragFrom(null);
              setDragOver(null);
            }}
            onDragEnd={() => {
              setDragFrom(null);
              setDragOver(null);
            }}
            className={`flex items-center gap-2 rounded-sm border bg-white px-1.5 py-1 ${
              dragOver === i && dragFrom !== i ? 'border-purple-400 ring-1 ring-purple-300' : 'border-slate-200'
            } ${dragFrom === i ? 'opacity-50' : ''}`}
          >
            <span
              className="cursor-grab text-slate-400 hover:text-slate-600"
              title="Drag to reorder"
              aria-hidden="true"
            >
              <GripVertical className="h-4 w-4" />
            </span>
            <span className="w-5 shrink-0 text-center text-xs font-semibold tabular-nums text-slate-500">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <select
                aria-label={`Product ${i + 1}`}
                name={`product-${index}-${i}`}
                value={line.productId}
                disabled={disabled}
                onChange={(e) => {
                  const id = e.target.value;
                  const option = options.find((o) => o.id === id);
                  const prefill = option && line.amount == null ? lineDefaultAmount(option) : null;
                  patch(i, {
                    productId: id,
                    productName: option?.name ?? line.productName,
                    ...(prefill ?? {}),
                  });
                }}
                className="h-8 w-full rounded-sm border border-slate-300 bg-white px-1.5 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {productSelectOptions(options, line, 'Choose a product…').map((o) => (
                  <option key={o.value} value={o.value} disabled={o.value === '' && line.productId !== ''}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <WasteCodes option={options.find((o) => o.id === line.productId)} />
            <input
              type="number"
              min={0}
              aria-label={`Amount ${i + 1}`}
              value={line.amount ?? ''}
              disabled={disabled}
              onChange={(e) => {
                const raw = e.target.value;
                const n = raw === '' ? null : Number(raw);
                patch(i, { amount: n != null && Number.isFinite(n) && n >= 0 ? n : null });
              }}
              className="h-8 w-28 rounded-sm border border-slate-300 bg-white px-1.5 text-right text-xs font-semibold tabular-nums text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              aria-label={`Unit ${i + 1}`}
              value={line.unit}
              disabled={disabled}
              onChange={(e) => patch(i, { unit: e.target.value as CargoProductLineDraft['unit'] })}
              className="h-8 rounded-sm border border-slate-300 bg-white px-1 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="kg">kg</option>
              <option value="L">L</option>
            </select>
            <button
              type="button"
              aria-label={`Remove product ${i + 1}`}
              title="Remove"
              disabled={disabled}
              onClick={() => onChange(lines.filter((_, j) => j !== i))}
              className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * A product's waste codes, read-only: the default, and "+N" when it has more —
 * hover to see them all. A tank can carry a mix, so nothing is picked here.
 */
function WasteCodes({ option }: { option: CargoProductOption | undefined }) {
  const codes = option?.wasteCodes ?? [];
  if (codes.length === 0) return null;
  const shown = option?.ewcCode ?? codes[0]!.code;
  const others = codes.length - 1;
  return (
    <span className="group relative inline-flex shrink-0 items-center gap-1">
      <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-700">
        {shown}
      </span>
      {others > 0 && (
        <>
          <span
            tabIndex={0}
            aria-label={`${codes.length} waste codes`}
            className="cursor-default rounded-full bg-slate-200 px-1.5 text-[10px] font-semibold text-slate-600 outline-none focus:ring-2 focus:ring-blue-500"
          >
            +{others}
          </span>
          <span
            role="tooltip"
            className="pointer-events-none invisible absolute right-0 top-full z-30 mt-1 w-80 rounded-md border border-slate-200 bg-white p-2 text-left text-[11px] text-slate-700 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
          >
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Waste codes (EWC)
            </span>
            {codes.map((c) => (
              <span key={c.code} className="flex items-start gap-2 py-0.5">
                <span className="w-16 shrink-0 font-mono font-semibold text-slate-800">{c.code}</span>
                <span className="min-w-0 flex-1">{c.description}</span>
                {c.code === shown && <span className="shrink-0 text-[10px] text-purple-600">default</span>}
              </span>
            ))}
          </span>
        </>
      )}
    </span>
  );
}

/**
 * "— None —" (or a prompt), the offered products, and — kept visible rather
 * than silently dropped on the next save — a product this line already
 * carries that is no longer offered (unlinked, deactivated).
 */
function productSelectOptions(
  options: CargoProductOption[],
  line: CargoProductLineDraft | null,
  emptyLabel = '— None —',
): Array<{ value: string; label: string }> {
  const out = [
    { value: '', label: emptyLabel },
    ...options.map((o) => ({ value: o.id, label: productOptionLabel(o) })),
  ];
  if (line?.productId && !options.some((o) => o.id === line.productId)) {
    out.push({ value: line.productId, label: `${line.productName ?? 'Product'} (no longer offered)` });
  }
  return out;
}
